import { tenantApi } from '@shared/api/tenantApi';
import { unwrapList } from '@shared/api/unwrap';

export interface MapPin {
  pin_id: string;
  type: 'property' | 'amenity' | 'security' | 'other';
  label: string;
  description?: string;
  lat: number;
  lng: number;
  icon_url?: string;
}

export interface MapPolygon {
  polygon_id: string;
  label: string;
  coordinates: Array<{ lat: number; lng: number }>;
  fill_color?: string;
  stroke_color?: string;
}

export interface MapData {
  center: { lat: number; lng: number };
  zoom: number;
  pins: MapPin[];
  polygons: MapPolygon[];
  style_url?: string;
}

interface RawLocation {
  location_id: string;
  name: string;
  address?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  type_name?: string;
  status_name?: string;
}

function parseCoord(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

function inferPinType(typeName?: string): MapPin['type'] {
  const n = (typeName ?? '').toLowerCase();
  if (/secur|gate|patrol/.test(n)) return 'security';
  if (/amenity|facility|club|pool|golf/.test(n)) return 'amenity';
  if (/unit|plot|stand|property|home|house/.test(n)) return 'property';
  return 'other';
}

/**
 * Estate map — locations with coordinates from `GET /locations`.
 * Same source as the Angular member portal map (Leaflet + OSM on web).
 */
export async function fetchMapData(): Promise<MapData | null> {
  try {
    const res = await tenantApi.get('/locations', { params: { limit: 100, page: 1 } });
    const list = unwrapList<RawLocation>(res.data);

    const pins = list
      .map((loc) => {
        const lat = parseCoord(loc.latitude);
        const lng = parseCoord(loc.longitude);
        if (lat == null || lng == null) return null;
        const pin: MapPin = {
          pin_id: loc.location_id,
          type: inferPinType(loc.type_name),
          label: loc.name,
          description: loc.address ?? loc.type_name ?? undefined,
          lat,
          lng,
        };
        return pin;
      })
      .filter((pin): pin is MapPin => pin !== null);

    if (pins.length === 0) return null;

    const center = {
      lat: pins.reduce((sum, p) => sum + p.lat, 0) / pins.length,
      lng: pins.reduce((sum, p) => sum + p.lng, 0) / pins.length,
    };

    return {
      center,
      zoom: pins.length === 1 ? 15 : 14,
      pins,
      polygons: [],
    };
  } catch {
    return null;
  }
}
